import React, { useMemo, useState } from 'react';
import { ClickAwayListener, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Loading } from 'src/commons';
import { grey } from 'src/theme/colors';
import { ZoneFilterOption } from '../types';

const useStyles = makeStyles({
  wrap: {
    position: 'relative',
    width: '100%',
  },
  control: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 36,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
    background: '#fff',
    cursor: 'pointer',
    transition: 'border-color .15s',
    paddingRight: 28,
    '&:hover': {
      borderColor: '#1a1a18',
    },
  },
  controlOpen: {
    borderColor: '#E8336A',
    borderWidth: 2,
    paddingRight: 27,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    padding: '4px 8px',
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
  },
  placeholder: {
    color: grey.textPrimary,
    fontSize: 13,
    paddingLeft: 4,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#f1efe8',
    color: '#1a1a18',
    border: '0.5px solid #d3d1c7',
    borderRadius: 4,
    padding: '2px 6px',
    fontSize: 12,
    fontWeight: 500,
    maxWidth: '100%',
  },
  chipLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  remove: {
    cursor: 'pointer',
    color: '#6b6a66',
    fontSize: 14,
    lineHeight: 1,
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    flexShrink: 0,
    '&:hover': {
      color: '#a32d2d',
    },
  },
  arrow: {
    position: 'absolute',
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    color: grey.textPrimary,
    pointerEvents: 'none',
    fontSize: 22,
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 2px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: '0.5px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,.08)',
    zIndex: 10,
    maxHeight: 220,
    overflowY: 'auto',
  },
  option: {
    width: '100%',
    textAlign: 'left',
    background: 'none',
    fontFamily: 'inherit',
    color: '#1a1a18',
    padding: '9px 14px',
    fontSize: 13,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: 'none',
    borderBottom: '0.5px solid rgba(0, 0, 0, 0.1)',
    transition: 'background .1s',
    '&:hover': {
      background: '#fbeaf0',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  optionSelected: {
    background: '#fbeaf0',
    color: '#72243e',
  },
  checkIcon: {
    fontSize: 16,
    color: '#E8336A',
  },
  loadingWrap: {
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
  },
});

interface ZoneMultiSelectProps {
  options: ZoneFilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  loading?: boolean;
}

export const ZoneMultiSelect = ({
  options,
  selected,
  onChange,
  placeholder = 'Zona',
  loading = false,
}: ZoneMultiSelectProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const optionsById = useMemo(
    () => Object.fromEntries(options.map((option) => [option.id, option.name])),
    [options],
  );

  const toggle = (zoneId: string) => {
    if (selected.includes(zoneId)) {
      onChange(selected.filter((item) => item !== zoneId));
    } else {
      onChange([...selected, zoneId]);
    }
  };

  const removeChip = (event: React.MouseEvent, zoneId: string) => {
    event.stopPropagation();
    onChange(selected.filter((item) => item !== zoneId));
  };

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <Loading />
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classes.wrap}>
        <div
          role="button"
          tabIndex={0}
          className={`${classes.control} ${open ? classes.controlOpen : ''}`}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          <div className={classes.chips}>
            {selected.length === 0 && <span className={classes.placeholder}>{placeholder}</span>}
            {selected.map((zoneId) => (
              <span key={zoneId} className={classes.chip}>
                <span className={classes.chipLabel}>{optionsById[zoneId] ?? zoneId}</span>
                <button
                  type="button"
                  className={classes.remove}
                  onClick={(event) => removeChip(event, zoneId)}
                  aria-label={`Quitar ${optionsById[zoneId] ?? zoneId}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <ExpandMoreIcon className={classes.arrow} />
        </div>
        {open && (
          <div className={classes.dropdown}>
            {options.map((option) => {
              const isSelected = selected.includes(option.id);
              return (
                <button
                  type="button"
                  key={option.id}
                  className={`${classes.option} ${isSelected ? classes.optionSelected : ''}`}
                  onClick={() => toggle(option.id)}
                >
                  <span>{option.name}</span>
                  {isSelected && <CheckIcon className={classes.checkIcon} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};
